"use client";
import * as Dialog from "@radix-ui/react-dialog";
import { signIn, useSession } from "next-auth/react";
import { env } from "next-runtime-env";
import { useEffect, useRef } from "react";
import "@/app/globals.css";
import { Box, Button, Flex } from "@radix-ui/themes";
import Image from "next/image";
import { HelpCircle } from "@/public/index";
import useTNStore from "@/stores/TNStore";
import useUBStore from "@/stores/UBStore";
import useVFCCSessionStore from "@/stores/VFCCSessionStore";
import { HALOB_PROVIDER, HELP_URL } from "@/utils/constants";

interface SignInDialogProps {
    open: boolean;
    setOpen?: (open: boolean) => void;
    modal?: boolean;
    callbackUrl?: string;
    onStatusChange?: (status: "loading" | "success" | "error") => void;
}

function SignInDialog({ open, setOpen, modal, callbackUrl, onStatusChange }: SignInDialogProps) {
    const { status } = useSession();
    const dialogRef = useRef<HTMLDivElement | null>(null); // Step 1: Create a ref with type assertion

    const { clearVFCCSession } = useVFCCSessionStore();
    const { clearTN } = useTNStore();
    const { clearUbid } = useUBStore();

    useEffect(() => {
        if (!onStatusChange) return;
        if (status === "authenticated") {
            onStatusChange("success");
        } else if (status === "unauthenticated") {
            onStatusChange("error");
        }
    }, [status, onStatusChange]);

    const handleSignIn = async () => {
        if (onStatusChange) onStatusChange("loading");
        if (setOpen) setOpen(false);
        clearVFCCSession();
        clearTN();
        clearUbid();
        await signIn(HALOB_PROVIDER, {
            callbackUrl: callbackUrl ?? window.location.href,
        });
    };

    return (
        <Dialog.Root open={open} onOpenChange={setOpen} modal={modal}>
            <Dialog.Portal>
                <Dialog.Overlay className="DialogOverlay" />
                <Dialog.Content ref={dialogRef} className="DialogContent" style={{ borderRadius: "16px" }}>
                    <Flex direction="column" justify="center" align="center">
                        <Box>
                            <Image src="/logo.png" alt="AT&T Config Portal" width={80} height={80} className="mr-2" priority={true} />
                        </Box>
                    </Flex>
                    <Dialog.Title
                        className="DialogTitle"
                        style={{
                            textAlign: "center",
                            fontSize: "34px",
                            fontFamily: "ATTAleckSans",
                            color: "black",
                            fontWeight: "bold",
                            marginBottom: 30,
                            lineHeight: "39.1px",
                        }}>
                        Sign in to your AT&T Business Voice Settings
                    </Dialog.Title>
                    <Dialog.Description
                        className="DialogDescription"
                        style={{
                            textAlign: "center",
                            fontSize: "18px",
                            fontFamily: "ATTAleckSans",
                            color: "black",
                        }}>
                        Manage your voice settings, configure schedules, add a business attendant, access logs, and explore other features.
                    </Dialog.Description>
                    <Box style={{ marginTop: 25 }}>
                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}>
                            <Button
                                className={"text-white nav-link bg-att-cobalt-blue mx-5 my-5 py-2 px-5 rounded-full text-center align-middle text-base"}
                                onClick={() => handleSignIn()}
                                style={{
                                    cursor: "pointer",
                                    fontFamily: "ATTAleckSans",
                                    fontSize: "14px",
                                    fontWeight: 700,
                                }}>
                                {"Sign in with AT&T Business Center credentials"}
                            </Button>
                        </Box>
                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}>
                            <Button
                                onClick={() => window.open(env("NEXT_PUBLIC_HELP_URL") ?? HELP_URL, "_blank")}
                                tabIndex={0}
                                className="rounded-full cursor-pointer h-11 min-w-24 px-[9px] py-[11px]  text-sm font-medium text-att-cobalt-blue flex justify-center items-center gap-1 font-face">
                                <HelpCircle />
                                Help
                            </Button>
                        </Box>
                    </Box>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

export default SignInDialog;
